import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes } from "react-router-dom";

import { EventWorkspaceLayout } from "./components/layout/EventWorkspaceLayout";
import { PremiumShellBackdrop } from "./components/layout/PremiumShellBackdrop";
import { WorkspaceLayout } from "./components/layout/WorkspaceLayout";
import { AppErrorBoundary } from "./components/feedback/AppErrorBoundary";
import { APP_WORKSPACE } from "./constants/appWorkspace";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { getRole, isOwner, roleHomeRoute } from "./utils/roles";
import type { UserRole } from "./types";
import { WorkspaceProvider } from "./context/WorkspaceContext";
import AuthGatewayPage from "./pages/AuthGateway/AuthGatewayPage";
import LandingPage from "./pages/Landing/LandingPage";
import ArtistsPage from "./pages/Artists/ArtistsPage";
import CalendarPage from "./pages/Calendar/CalendarPage";
import ContactsPage from "./pages/Contacts/ContactsPage";
import DashboardPage from "./pages/Dashboard/DashboardPage";
import EventOSPage from "./pages/EventOS/EventOSPage";
import EventContactsTab from "./pages/Events/EventContactsTab";
import EventEditPage from "./pages/Events/EventEditPage";
import EventInvoiceGeneratePage from "./pages/Events/EventInvoiceGeneratePage";
import EventInvoiceManagePage from "./pages/Events/EventInvoiceManagePage";
import EventInvoicesLayout from "./pages/Events/EventInvoicesLayout";
import EventOverviewPage from "./pages/Events/EventOverviewPage";
import EventPnlPage from "./pages/Events/EventPnlPage";
import EventLogisticsPage from "./pages/Events/EventLogisticsPage";
import EventNotesTab from "./pages/Events/EventNotesTab";
import EventFilesTab from "./pages/Events/EventFilesTab";
import EventOnboardingPage from "./pages/Events/EventOnboardingPage";
import EventDealsPage from "./pages/Events/EventDealsPage";
import EventRsvpTab from "./pages/Events/EventRsvpTab";
import EventInvitesTab from "./pages/Events/EventInvitesTab";
import EventsAnalyticsPage from "./pages/Events/EventsAnalyticsPage";
import EventsPage from "./pages/Events/EventsPage";
import { FinanceLayout } from "./pages/Finance/FinanceLayout";
import FinanceInvoicesPage from "./pages/Finance/FinanceInvoicesPage";
import FinancePortfolioPage from "./pages/Finance/FinancePortfolioPage";
import FinanceEventsPage from "./pages/Finance/FinanceEventsPage";
import GuestListPage from "./pages/Guests/GuestListPage";
import HomePage from "./pages/Home/HomePage";
import TicketingLinksAdminPage from "./pages/Admin/TicketingLinksAdminPage";
import ProfilePage from "./pages/Profile/ProfilePage";
import PublicRsvpPage from "./pages/PublicRsvp/PublicRsvpPage";
import PublicInvitePage from "./pages/PublicInvite/PublicInvitePage";
import InvitePayPage from "./pages/PublicInvite/InvitePayPage";
import PublicGuestListPage from "./pages/PublicRsvp/PublicGuestListPage";
import PublicWalkInPage from "./pages/WalkIn/PublicWalkInPage";
import PublicPassPage from "./pages/PublicPass/PublicPassPage";
import GuestStatusPage from "./pages/PublicPass/GuestStatusPage";
import VenuesPage from "./pages/Venues/VenuesPage";
import { GoogleAnalytics } from "./components/GoogleAnalytics";
import { SeoHead } from "./components/SeoHead";
import { LegacyEventDashboardRedirect, LegacyPrefixedRedirect } from "./routes/LegacyRedirects";

const WS = APP_WORKSPACE;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: 0,
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode; allow?: Array<UserRole> }> = ({
  children,
  allow,
}) => {
  const { token, user } = useAuth();
  if (!token) {
    return <Navigate to="/" replace />;
  }
  if (allow && allow.length > 0 && !isOwner(user) && !allow.includes(getRole(user))) {
    return <Navigate to={roleHomeRoute(user)} replace />;
  }
  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token, user } = useAuth();
  if (token) {
    return <Navigate to={roleHomeRoute(user)} replace />;
  }
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Toaster
        position="bottom-left"
        /* Clears the fixed mobile tab bar (~68px + safe area), which would
           otherwise sit on top of every toast. No effect on desktop, where
           the offset resolves to 0. */
        containerStyle={{ bottom: "calc(var(--toast-offset, 0px) + 16px)" }}
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--color-toast-bg)",
            color: "var(--color-text)",
            border: "1px solid var(--color-toast-border)",
            fontFamily: "var(--font-sans)",
            fontSize: "var(--text-body-size)",
            boxShadow: "var(--shadow-toast)",
          },
          success: {
            iconTheme: { primary: "var(--color-success)", secondary: "var(--color-toast-bg)" },
          },
          error: {
            iconTheme: { primary: "var(--color-danger)", secondary: "var(--color-toast-bg)" },
          },
        }}
      />
      <AuthProvider>
        <WorkspaceProvider>
          <Router>
            <SeoHead />
            <GoogleAnalytics />
            <PremiumShellBackdrop />
            <div className="zoniq-app-frame">
              <Routes>
                <Route
                  path="/"
                  element={
                    <PublicRoute>
                      <LandingPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/auth"
                  element={
                    <PublicRoute>
                      <AuthGatewayPage />
                    </PublicRoute>
                  }
                />

                {/* Legacy workspace picker → calendar */}
                <Route path="/workspace" element={<Navigate to="/calendar" replace />} />

                {/* Unified app shell */}
                <Route
                  element={
                    <ProtectedRoute>
                      <WorkspaceLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route
                    element={
                      <AppErrorBoundary>
                        <Outlet />
                      </AppErrorBoundary>
                    }
                  >
                  <Route path="/home" element={<ProtectedRoute><HomePage workspace={WS} /></ProtectedRoute>} />
                  <Route
                    path="/calendar"
                    element={
                      <ProtectedRoute allow={["promoter"]}>
                        <CalendarPage workspace={WS} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/events"
                    element={
                      <ProtectedRoute allow={["operator", "promoter"]}>
                        <EventsPage workspace={WS} />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/events/analytics" element={<Navigate to="/finance/analytics" replace />} />
                  <Route
                    path="/finance"
                    element={
                      <ProtectedRoute allow={["promoter"]}>
                        <FinanceLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<FinancePortfolioPage />} />
                    <Route path="analytics" element={<EventsAnalyticsPage workspace={WS} />} />
                    <Route path="events" element={<FinanceEventsPage />} />
                    <Route path="invoices" element={<FinanceInvoicesPage />} />
                  </Route>
                  <Route path="/events/new" element={<ProtectedRoute><EventOnboardingPage workspace={WS} /></ProtectedRoute>} />
                  <Route
                    path="/events/:eventId/edit"
                    element={<ProtectedRoute><EventEditPage workspace={WS} /></ProtectedRoute>}
                  />

                  <Route path="/events/:eventId" element={<EventWorkspaceLayout />}>
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route
                      path="overview"
                      element={<ProtectedRoute><EventOverviewPage workspace={WS} /></ProtectedRoute>}
                    />
                    <Route
                      path="deals"
                      element={
                        <ProtectedRoute allow={["promoter"]}>
                          <EventDealsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="budget"
                      element={
                        <ProtectedRoute>
                          <EventLogisticsPage workspace={WS} embedMode defaultSection="budgets" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="pnl"
                      element={
                        <ProtectedRoute allow={["promoter"]}>
                          <EventPnlPage workspace={WS} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="run-of-show"
                      element={
                        <ProtectedRoute>
                          <EventLogisticsPage workspace={WS} embedMode defaultSection="schedules" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="tickets"
                      element={
                        <ProtectedRoute allow={["operator"]}>
                          <DashboardPage workspace={WS} embedMode view="tickets" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="live"
                      element={
                        <ProtectedRoute allow={["operator"]}>
                          <EventOSPage workspace={WS} embedMode area="event-os" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="rsvp"
                      element={
                        <ProtectedRoute allow={["operator", "promoter"]}>
                          <EventRsvpTab />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="invites"
                      element={
                        <ProtectedRoute allow={["operator"]}>
                          <EventInvitesTab />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="invoices"
                      element={
                        <ProtectedRoute allow={["promoter"]}>
                          <EventInvoicesLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<Navigate to="generate" replace />} />
                      <Route
                        path="generate"
                        element={<EventInvoiceGeneratePage workspace={WS} embedMode />}
                      />
                      <Route path="manage" element={<EventInvoiceManagePage workspace={WS} />} />
                    </Route>
                    <Route
                      path="contacts"
                      element={
                        <ProtectedRoute allow={["promoter"]}>
                          <EventContactsTab />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="notes"
                      element={
                        <ProtectedRoute allow={["promoter"]}>
                          <EventNotesTab />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="files"
                      element={
                        <ProtectedRoute allow={["promoter"]}>
                          <EventFilesTab />
                        </ProtectedRoute>
                      }
                    />
                  </Route>

                  <Route path="/directory/artists" element={<ProtectedRoute><ArtistsPage workspace={WS} /></ProtectedRoute>} />
                  <Route path="/directory/venues" element={<ProtectedRoute><VenuesPage workspace={WS} /></ProtectedRoute>} />
                  <Route
                    path="/directory/contacts"
                    element={
                      <ProtectedRoute allow={["promoter"]}>
                        <ContactsPage workspace={WS} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/directory/guests"
                    element={
                      <ProtectedRoute allow={["promoter"]}>
                        <GuestListPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/admin/ticketing-links" element={<ProtectedRoute><TicketingLinksAdminPage /></ProtectedRoute>} />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute allow={["operator", "promoter"]}>
                        <ProfilePage workspace={WS} />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/guests" element={<Navigate to="/directory/guests" replace />} />
                  </Route>
                </Route>

                {/* Public RSVP / Guest List / Walk-in forms — reachable while logged in (share link / QR) */}
                <Route path="/rsvp/:inviteToken" element={<PublicRsvpPage />} />
                <Route path="/i/pay/:payToken" element={<InvitePayPage />} />
                <Route path="/i/:token" element={<PublicInvitePage />} />
                <Route path="/guest-list/:inviteToken" element={<PublicGuestListPage />} />
                <Route path="/walk-in/:inviteToken" element={<PublicWalkInPage />} />
                <Route path="/pass/:token" element={<PublicPassPage />} />
                <Route path="/guest/:token" element={<GuestStatusPage />} />

                {/* Legacy prefixed routes */}
                <Route path="/event-os/*" element={<LegacyPrefixedRedirect />} />
                <Route path="/pnl/*" element={<LegacyPrefixedRedirect />} />

                {/* Legacy flat shortcuts */}
                <Route path="/dashboard" element={<Navigate to="/events" replace />} />
                <Route path="/command-center" element={<Navigate to="/events" replace />} />
                <Route path="/events/:eventId/dashboard" element={<LegacyEventDashboardRedirect />} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </Router>
        </WorkspaceProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
